import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isProcessingAuth, setIsProcessingAuth] = useState(false);

  // Process session_id from URL fragment on mount
  useEffect(() => {
    const processSessionId = async () => {
      // Check for session_id in URL fragment
      const fragment = window.location.hash.substring(1);
      const params = new URLSearchParams(fragment);
      const sessionId = params.get('session_id');

      if (sessionId) {
        console.log('Processing session_id from OAuth redirect...');
        setIsProcessingAuth(true);
        try {
          // Call backend to process session
          const response = await axios.post(
            `${API_URL}/api/auth/google/process-session`,
            {},
            {
              headers: {
                'X-Session-ID': sessionId,
              },
              withCredentials: true,
            }
          );

          const { user: userData, session_token } = response.data;
          
          // Store session token in localStorage as fallback
          localStorage.setItem('session_token', session_token);
          
          console.log('Google OAuth authentication successful', userData);
          
          // Clean URL fragment
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
          
          // Set user state
          setUser(userData);
          setIsProcessingAuth(false);
          
          // Check if there's a saved redirect path
          const redirectPath = localStorage.getItem('oauth_redirect_path');
          if (redirectPath && redirectPath !== '/') {
            localStorage.removeItem('oauth_redirect_path');
            // Small delay to ensure state is set, then reload to the intended page
            setTimeout(() => {
              window.location.href = redirectPath;
            }, 100);
          }
          
        } catch (error) {
          console.error('Failed to process session:', error);
          // Clean URL fragment even on error
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
          setIsProcessingAuth(false);
          // Clean up redirect path on error
          localStorage.removeItem('oauth_redirect_path');
        }
      } else {
        // No session_id, check for existing session (non-blocking)
        checkExistingSession();
      }
    };

    processSessionId();
  }, []);

  const checkExistingSession = async () => {
    try {
      // Try cookie-based authentication first
      const response = await axios.get(`${API_URL}/api/auth/google/session`, {
        withCredentials: true,
      });
      if (response.data) {
        setUser(response.data);
        console.log('Session restored from cookie');
      }
    } catch (error) {
      // Silent fail for cookie check - expected if not logged in
      console.log('No cookie session found, trying localStorage');
      
      // Try fallback with localStorage token
      const token = localStorage.getItem('session_token');
      if (token) {
        try {
          const response = await axios.get(`${API_URL}/api/auth/google/session`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          });
          if (response.data) {
            setUser(response.data);
            console.log('Session restored from localStorage token');
          }
        } catch (err) {
          console.log('No valid session found - user not logged in');
          localStorage.removeItem('session_token');
        }
      } else {
        console.log('No session token found - fresh visit');
      }
    }
    // Don't set loading state here - let page render immediately
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(
        `${API_URL}/api/auth/google/logout`,
        {},
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
        }
      );
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('session_token');
      setUser(null);
      // Redirect to homepage
      window.location.href = '/';
    }
  };

  const openAuthModal = () => {
    setShowAuthModal(true);
  };

  const closeAuthModal = () => {
    setShowAuthModal(false);
  };

  const value = {
    user,
    loading,
    isProcessingAuth,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false,
    logout,
    showAuthModal,
    openAuthModal,
    closeAuthModal,
  };

  // Show loading screen during OAuth processing
  if (isProcessingAuth) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-yellow mx-auto mb-4"></div>
          <p className="text-gray-400 text-lg">Completing sign in...</p>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
